<template>
  <div id="user" class="p-0 bg-gray">
    <UserHeader
      v-if="!isOpenSite && isMobile"
      :user="user"
      :overwrite-title="overwriteTitle"
      :overwrite-tree="overwriteTree"
      style="position: sticky; top: 0; z-index: 100; background-color: white"
    />
    <div
      class="users-router-view d-flex flex-row h-100 w-100"
      :style="{
        'background-color': isOpenSite ? 'transparent' : '#f7f6f4', // '#f2eee9'
        'max-height': isOpenSite ? 'auto' : '100vh',
      }"
    >
      <div
        class="p-2 w-100 overflow-scroll"
        :class="{ 'ps-3': !isMobile }"
        style="height: auto"
      >
        <div
          v-if="!isOpenSite && !isMobile"
          class="mb-2 py-3"
          id="user-page-title-card"
          @vue:mounted="() => (afetrTitleActionSpaceMounted = true)"
        >
          <div class="d-flex justify-content-between">
            <h4 class="d-flex align-items-center">
              <PageTree
                v-if="overwriteTree?.length || pageTree?.length"
                :tree="overwriteTree || pageTree"
              />
              <b
                id="user-page-header-title"
                style="border-bottom: 2px solid #999"
              >
                {{ overwriteTitle || pageTitle }}
              </b>
            </h4>
            <div class="d-flex align-items-center me-2 position-relative">
              <div id="page-title-action-space" class="me-2"></div>
              <Button
                id="notification-button"
                icon="pi pi-bell"
                text
                rounded
                aria-haspopup="true"
                aria-controls="overlay_notifications"
                class="text-black border-0"
                :badge="unreadNotifications.length ? '-' : undefined"
                @click="handleClick"
              />

              <Menu
                ref="notification"
                id="overlay_notificatios"
                :model="unreadNotificationMenuitems"
                popup
              />

              <div
                v-if="showEmptyPanel"
                class="dropdown-menu show p-3 shadow"
                style="
                  position: absolute;
                  right: 0;
                  top: 100%;
                  min-width: 200px;
                "
              >
                <span class="text-muted small">通知はありません</span>
              </div>
            </div>
          </div>
        </div>

        <router-view
          class="user-router-view"
          :class="{ 'is-open-site': isOpenSite }"
          :style="{
            'max-width': isMobile ? '100%' : 'calc(100vw - 205px - 1rem)',
          }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useUser,
  useRouterUtil,
  useAuthorize,
  useMqUtils,
} from "/@/vue/composables";
import { UserHeader, PageTree } from "/@/vue/components/Layouts";
import { computed, provide, ref, watch } from "vue";
import Button from "primevue/button";
import Menu from "primevue/menu";
import { notificationTypeLabel } from "/@/types";

const { isMobile } = useMqUtils();

const {
  isOpenSite,
  currentRouteName,
  currentRouteQuery,
  currentRouteMeta,
  goto,
} = useRouterUtil();

const {
  user,
  notifications,
  unreadNotifications,
  gotoNotifiable,
  notifiableIcon,
} = useUser();

const unreadNotificationMenuitems = computed(() => {
  const maxToShow = 3;
  const baseItems = unreadNotifications.value
    .slice(0, maxToShow)
    .map((notification) => ({
      label: notificationTypeLabel(notification.actionType),
      icon: `pi ${notifiableIcon(notification)}`,
      command: () => {
        // 通知をクリックしたときの処理
        gotoNotifiable(notification);
      },
    }));

  const moreCount = unreadNotifications.value.length - maxToShow;
  if (moreCount > 0) {
    baseItems.push({
      label: `さらに ${moreCount} 件の通知`,
      icon: "pi pi-ellipsis-h",
      command: () => {
        goto({ name: "UsersManagementIndex" });
      },
    });
  }

  return baseItems;
});

const afetrTitleActionSpaceMounted = ref(false);
provide("afetrTitleActionSpaceMounted", afetrTitleActionSpaceMounted);

const pageTitle = computed(() => {
  return currentRouteMeta.value?.title;
});

const pageTree = computed(() => {
  return currentRouteMeta.value?.tree || [];
});

// overwrite breadcrumb

const isInvest24 = computed(() => {
  return currentRouteQuery.value.recruitmentType === "invest24";
});

const overwriteTitle = computed(() => {
  let title;

  if (currentRouteName.value === "UsersRecruitmentsManagementsManagement") {
    if (isInvest24.value) {
      title = "奥能登実態調査ボランティア管理";
    } else {
      title = "1.5次避難所調査ボランティア管理";
    }

    if (currentRouteQuery.value.area) {
      title += `（${currentRouteQuery.value.area}）`;
    }
  }

  return title;
});

const overwriteTree = computed(() => {
  if (currentRouteName.value === "UsersRecruitmentsShow") {
    return [
      {
        title: "ボランティア管理",
        name: "UsersRecruitmentsManagementsIndex",
      },
      {
        title: isInvest24.value
          ? "奥能登実態把調査ボランティア管理"
          : "1.5次避難所ボランティア管理",
        name: "UsersRecruitmentsManagementsManagement",
        query: { recruitmentType: currentRouteQuery.value.recruitmentType },
      },
    ];
  } else if (currentRouteName.value === "UsersRecruitmentsEdit") {
    return [
      {
        title: "ボランティア管理",
        name: "UsersRecruitmentsManagementsIndex",
      },
      {
        title: isInvest24.value
          ? "奥能登実態把調査ボランティア管理"
          : "1.5次避難所ボランティア管理",
        name: "UsersRecruitmentsManagementsManagement",
        query: { recruitmentType: currentRouteQuery.value.recruitmentType },
      },
      {
        title: "ボランティア詳細",
        name: "UsersRecruitmentsShow",
        params: { id: currentRouteQuery.value.id },
        query: {
          targetDate: currentRouteQuery.value.targetDate,
          recruitmentType: currentRouteQuery.value.recruitmentType,
        },
      },
    ];
  }
});

// notification menu

const notification = ref();
const showEmptyPanel = ref(false);

function handleClick(event: MouseEvent) {
  if (notifications.value.length === 0) {
    showEmptyPanel.value = !showEmptyPanel.value;
  } else {
    showEmptyPanel.value = false;
    notification.value.toggle(event);
  }
}
</script>

<style lang="scss" scoped>
.user-header {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

#user {
  min-height: calc(100vh - 2rem - 100px);
}

.user-router-view {
  padding-bottom: 1rem;

  &.is-open-site {
    max-width: calc(100vw - 1rem);
  }
}
</style>

<style lang="scss">
#user-nav-menu-card {
  .p-card-body {
    padding: 1rem 0.5rem;
  }

  .p-tieredmenu {
    border: none;
  }
}

#notification-button {
  .pi {
    font-size: 1.2rem;
  }

  .p-badge.p-component {
    background: red;
    margin-left: -1rem;
    margin-top: -1rem;
  }
}
</style>
